/**
 * Top-level CMS and marketing collections. Corresponding document types under `firestore/cms/` use
 * `created_on` / `updated_on` (or `updated_on` only where noted) for editors and server jobs.
 *
 * **Firestore rules:** regular app users have `write: false` on most of these paths; `isAdminUser()`
 * can write some (e.g. audioCourses). Admin SDK bypasses rules. Prefer FireCMS `autoValue` for
 * timestamps on CMS-backed types — no extra client security rule is needed for normal users.
 *
 * | Collection (enum value)   | Type module (`firestore/cms/…`) | Audit fields on stored doc   |
 * | ------------------------- | ------------------------------ | ---------------------------- |
 * | AudioCourses              | `audioCourse`                  | created_on, updated_on       |
 * | IntroCourses              | `introCourse`                  | created_on, updated_on       |
 * | MeditationDailies         | `meditationDaily`              | created_on, updated_on       |
 * | Authors                   | `author`                       | created_on, updated_on       |
 * | Developers                | `developer`                    | created_on, updated_on       |
 * | Content / ContentWeb      | `content`                      | created_on, updated_on       |
 * | BlogArticles              | `blogArticle`                  | created_on, updated_on       |
 * | PodcastEpisodes           | `podcastEpisode`               | created_on, updated_on       |
 * | OnlineCoursesWeb          | `onlineCourseWeb`              | created_on, updated_on       |
 * | InsuranceCheckFormData    | `insuranceCheckFormData`       | updated_on only              |
 * | InstagramLinks            | `instagramLinks`               | updated_on only              |
 * | PromoCodes                  | `promoCodes`                   | created_on, updated_on (no client access) |
 */
export enum CMSCollections {
  AudioCourses = "audioCourses",
  VideoCourses = "videoCourses",
  IntroCourses = "introCourses",
  OnlineCoursesWeb = "onlineCoursesWeb",
  MeditationDailies = "meditationDailies",
  Authors = "authors",
  Developers = "developers",
  Content = "content",
  ContentWeb = "contentWeb",
  BlogArticles = "blogArticles",
  PodcastEpisodes = "podcastEpisodes",
  InsuranceCheckFormData = "insuranceCheckFormData",
  InstagramLinks = "instagramLinks",
  PromoCodes = "promoCodes",
}

/**
 * Top-level Firestore collection IDs for **application/runtime** data (user tree, feedback, aggregate
 * indexes), distinct from {@link CMSCollections}.
 *
 * Not every member is “secret”: `Users` is the normal user document tree. `app/contentReferences.ts`
 * types use **`lastUpdated`** on index documents (Cloud Functions, client read-only). Server-only
 * `UserInternal` lives under `users/{uid}/internal/profile` and uses **`created_on` / `updated_on`**
 * where applicable — no client writes.
 */
export enum AppRootCollections {
  Users = "users",
  Feedback = "feedback",
  LegalRequests = "legalRequests",
  /** Server-only per-email counters for unauthenticated legal form rate limits. */
  LegalEmailThrottle = "legalEmailThrottle",
  MailDeliveryLogs = "mailDeliveryLogs",
  ContentReferences = "contentReferences", // Reference for diverse content
  /** Server-maintained public aggregates (e.g. registered user count for marketing). */
  PublicStats = "publicStats",
  ProfilePictures = "profilePictures",
}

/**
 * Subcollections under `users/{uid}` that are included in client account-deletion cleanup.
 * Shapes live in `app/users/**` type modules. Audit naming differs from CMS docs:
 *
 * - **User root** (`users/{uid}`): `UserDocument` — `created_on`, `updated_on` (enforced in Firestore rules).
 *   Nested **`Recommendation.created_on`** is set in app code when mutating `recommendations`.
 * - **activityLog**: `ActivityLog` — required **`timestamp`** on each entry (create via `addDoc`).
 * - **favorites**: `Favorite` — required **`timestamp`** on create (`setDoc`).
 * - **watchedFiles**: `WatchedFiles` — **`watchedOn`** on `singleton` or each `files[]` entry (nested; rules harder).
 * - **downloadAudit**: server-created audit rows for offline download key issuance; client can read/delete own rows
 *   only so account deletion can clean them up.
 * - **internal/downloadThrottle**: server-only counters for `requestDownload` rate limiting (not client-writable).
 * - **internal/contractCancellationThrottle**: server-only counters for `submitContractCancellation` rate limiting (not client-writable).
 * - **gamification**: user stats document(s) under `gamification/` (e.g. `stats`).
 *
 * **Not client-deletable:** `users/{uid}/internal/**` (see `UserInternalCollections`).
 */
export enum UserCollections {
  WatchedFiles = "watchedFiles",
  Favorites = "favorites",
  ActivityLog = "activityLog",
  DownloadAudit = "downloadAudit",
  Gamification = "gamification",
}

/** Document IDs under `users/{uid}/gamification/`. */
export enum GamificationDocuments {
  Stats = "stats",
}

// Server-only child of Users. Not included in UserCollections because clients must not read, write, or delete it.
export enum UserInternalCollections {
  Internal = "internal",
}

export enum MediaType {
  AudioCourses = CMSCollections.AudioCourses,
  // VideoCourses = CMSCollections.VideoCourses,
  IntroCourses = CMSCollections.IntroCourses,
  MeditationDailies = CMSCollections.MeditationDailies,
  /** App feature: user-guided silent meditation with configurable duration (no CMS document) */
  SilentMeditation = "silentMeditation",
}
